import React, { useState, useEffect } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, Coins, Percent, Info, CheckCircle2 } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../../components/RiskAnalysisBlock';

const CryptoProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  // Real-time calculation
  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, quantity, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const qty = parseFloat(quantity);
    const feeAmount = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(qty) || buy < 0 || sell < 0 || qty <= 0 || feeAmount < 0) {
      setResult(null);
      return;
    }

    const totalInvestment = buy * qty;
    const totalRevenue = sell * qty;
    const grossProfit = totalRevenue - totalInvestment;
    const netProfit = grossProfit - feeAmount;
    const roi = totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;
    const priceChange = buy > 0 ? ((sell - buy) / buy) * 100 : 0;
    const breakEvenPrice = qty > 0 ? (totalInvestment + feeAmount) / qty : 0;

    setResult({
      totalInvestment,
      totalRevenue,
      grossProfit,
      netProfit,
      roi,
      priceChange,
      breakEvenPrice,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setQuantity('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return prefix + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  };

  // Get result styling based on profit/loss
  const getResultStyle = (isProfitable, isBreakEven) => {
    if (isBreakEven) return { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400' };
    if (isProfitable) return { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' };
    return { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' };
  };

  // Quick scenarios
  const scenarios = [
    { label: 'BTC Example', buy: '30000', sell: '45000', qty: '0.5', fees: '50' },
    { label: 'ETH Example', buy: '2000', sell: '2500', qty: '2', fees: '25' },
    { label: 'SOL Example', buy: '100', sell: '150', qty: '10', fees: '5' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setQuantity(scenario.qty);
    setFees(scenario.fees);
  };

  return (
    <div className="space-y-6" data-testid="crypto-profit-calculator">
      {/* Quick Scenarios */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Coins size={18} className="text-yellow-400" />
          <h3 className="text-sm font-medium text-gray-300">Trade Details</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
              placeholder="e.g., 30000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="buy-price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Price per coin when you bought</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={sellPrice}
              onChange={(e) => setSellPrice(e.target.value)}
              placeholder="e.g., 45000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="sell-price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Price per coin when you sell (or target)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantity (Coins)
            </label>
            <input
              type="number"
              step="any"
              min="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g., 0.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="quantity-input"
            />
            <p className="text-xs text-gray-500 mt-1">Number of coins/tokens</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Fees ($) <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
              placeholder="e.g., 50"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="fees-input"
            />
            <p className="text-xs text-gray-500 mt-1">Exchange + network fees combined</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
            data-testid="clear-btn"
          >
            <Trash2 size={16} />
            Clear
          </button>
        </div>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result Card */}
          <div className={`p-6 rounded-lg border ${getResultStyle(result.isProfitable, result.isBreakEven).bg} ${getResultStyle(result.isProfitable, result.isBreakEven).border}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {result.isProfitable ? (
                  <TrendingUp className="text-green-400" size={24} />
                ) : result.isBreakEven ? (
                  <DollarSign className="text-yellow-400" size={24} />
                ) : (
                  <TrendingDown className="text-red-400" size={24} />
                )}
                <span className="text-lg font-medium text-gray-200">
                  {result.isBreakEven ? 'Break Even' : result.isProfitable ? 'Profit' : 'Loss'}
                </span>
              </div>
              <button
                onClick={() => copyResult(result.netProfit, 'profit')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title="Copy result"
              >
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            
            <div className="text-center">
              <p className={`text-4xl font-bold ${getResultStyle(result.isProfitable, result.isBreakEven).text}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-xl mt-2 ${getResultStyle(result.isProfitable, result.isBreakEven).text}`}>
                ROI: {formatPercent(result.roi)}
              </p>
            </div>
          </div>

          {/* Detailed Results Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Investment</p>
                  <p className="text-xl font-semibold text-white">{formatCurrency(result.totalInvestment)}</p>
                </div>
                <button
                  onClick={() => copyResult(result.totalInvestment, 'investment')}
                  className="p-2 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'investment' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Buy Price × Quantity</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Revenue</p>
                  <p className="text-xl font-semibold text-white">{formatCurrency(result.totalRevenue)}</p>
                </div>
                <button
                  onClick={() => copyResult(result.totalRevenue, 'revenue')}
                  className="p-2 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'revenue' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Sell Price × Quantity</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Price Change</p>
                  <p className={`text-xl font-semibold ${result.priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatPercent(result.priceChange)}
                  </p>
                </div>
                <Percent size={20} className="text-gray-500" />
              </div>
              <p className="text-xs text-gray-500 mt-1">From buy to sell price</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Gross Profit</p>
                  <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
                  </p>
                </div>
                <button
                  onClick={() => copyResult(result.grossProfit, 'gross')}
                  className="p-2 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'gross' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Before fees</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Fees Paid</p>
                  <p className="text-xl font-semibold text-orange-400">
                    {formatCurrency(parseFloat(fees) || 0)}
                  </p>
                </div>
                <DollarSign size={20} className="text-gray-500" />
              </div>
              <p className="text-xs text-gray-500 mt-1">Exchange + network fees</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Break-Even Price</p>
                  <p className="text-xl font-semibold text-blue-400">
                    {formatCurrency(result.breakEvenPrice)}
                  </p>
                </div>
                <button
                  onClick={() => copyResult(result.breakEvenPrice, 'breakeven')}
                  className="p-2 hover:bg-gray-700 rounded transition-colors"
                >
                  {copied === 'breakeven' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Min sell price to break even</p>
            </div>
          </div>

          {/* Summary Card */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Info size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Trade Summary</h4>
            </div>
            <div className="text-sm text-gray-400 space-y-1">
              <p>
                Buying <span className="text-white font-medium">{quantity}</span> coins at{' '}
                <span className="text-white font-medium">{formatCurrency(parseFloat(buyPrice))}</span> each costs{' '}
                <span className="text-white font-medium">{formatCurrency(result.totalInvestment)}</span>.
              </p>
              <p>
                Selling at <span className="text-white font-medium">{formatCurrency(parseFloat(sellPrice))}</span> each returns{' '}
                <span className="text-white font-medium">{formatCurrency(result.totalRevenue)}</span>.
              </p>
              <p>
                After <span className="text-orange-400 font-medium">{formatCurrency(parseFloat(fees) || 0)}</span> in fees,
                your net {result.isProfitable ? 'profit' : 'loss'} is{' '}
                <span className={`font-medium ${result.isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                  {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
                </span>{' '}
                ({formatPercent(result.roi)} ROI).
              </p>
            </div>
          </div>

          {/* Risk Analysis Block - NEW CRO ELEMENT */}
          <RiskAnalysisBlock 
            investmentAmount={result.totalInvestment}
            currentValue={result.totalRevenue}
            profit={result.netProfit}
          />

          {/* Affiliate Block */}
          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="crypto"
            toolName="Crypto Profit Calculator"
            toolSlug="crypto-profit-calculator"
            investmentAmount={result.totalInvestment}
          />
        </div>
      )}

      {/* Formula Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-purple-400" />
          <h4 className="text-sm font-medium text-gray-300">Formulas Used</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Total Investment:</p>
            <code className="text-purple-400">Buy Price × Quantity</code>
          </div>
          <div>
            <p className="text-gray-400">Total Revenue:</p>
            <code className="text-purple-400">Sell Price × Quantity</code>
          </div>
          <div>
            <p className="text-gray-400">Net Profit:</p>
            <code className="text-purple-400">Revenue - Investment - Fees</code>
          </div>
          <div>
            <p className="text-gray-400">ROI:</p>
            <code className="text-purple-400">(Net Profit / Investment) × 100</code>
          </div>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        
        {/* What is Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Crypto Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Crypto Profit Calculator</strong> is an essential tool for cryptocurrency traders and investors 
              that helps you determine your potential profit or loss from a crypto trade. By entering your buy price, 
              sell price, and the quantity of coins traded, you can instantly see your total investment, revenue, 
              net profit, and return on investment (ROI).
            </p>
            <p>
              Unlike traditional stock investments, cryptocurrency markets operate 24/7 with high volatility, making 
              profit calculation crucial for every trade. Our calculator accounts for trading fees, which can significantly 
              impact your actual returns, especially for frequent traders or those dealing with smaller position sizes.
            </p>
            <p>
              Whether you're trading Bitcoin, Ethereum, Solana, or any altcoin, this calculator provides accurate 
              profit projections to help you make informed trading decisions. It's particularly useful for planning 
              exit strategies, setting realistic profit targets, and understanding the true cost of trading including fees.
            </p>
          </div>
        </section>

        {/* How to Use Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Crypto Profit Calculator</h2>
          <div className="space-y-3">
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-white">Enter Your Buy Price</h3>
                <p className="text-gray-400 text-sm">Input the price per coin at which you purchased (or plan to purchase) the cryptocurrency. This is your entry price.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-white">Enter Your Sell Price</h3>
                <p className="text-gray-400 text-sm">Input the price at which you sold (or plan to sell) the cryptocurrency. This determines your exit point and potential profit.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-white">Enter the Quantity</h3>
                <p className="text-gray-400 text-sm">Specify how many coins you're trading. You can enter fractional amounts like 0.5 BTC or 2.75 ETH.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-white">Add Trading Fees (Optional)</h3>
                <p className="text-gray-400 text-sm">Include any exchange fees, network fees, or gas costs to get an accurate net profit calculation.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <h3 className="font-semibold text-white">View Your Results</h3>
                <p className="text-gray-400 text-sm">Instantly see your total investment, revenue, gross profit, net profit after fees, ROI percentage, and break-even price.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculation Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Crypto Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: Bitcoin Trade</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's say you want to calculate your profit from a Bitcoin trade:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $40,000 per BTC</li>
                <li><strong>Sell Price:</strong> $52,000 per BTC</li>
                <li><strong>Quantity:</strong> 0.5 BTC</li>
                <li><strong>Trading Fees:</strong> $50 (combined buy and sell fees)</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg">
                <p className="font-mono text-sm">
                  <span className="text-gray-400">Total Investment:</span> <span className="text-white">$40,000 × 0.5 = $20,000</span><br/>
                  <span className="text-gray-400">Total Revenue:</span> <span className="text-white">$52,000 × 0.5 = $26,000</span><br/>
                  <span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$26,000 - $20,000 = $6,000</span><br/>
                  <span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$6,000 - $50 = $5,950</span><br/>
                  <span className="text-gray-400">ROI:</span> <span className="text-blue-400">($5,950 / $20,000) × 100 = 29.75%</span>
                </p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                In this example, your $20,000 investment would yield a net profit of $5,950, representing a 29.75% return on investment after accounting for trading fees.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">How do I calculate crypto profit manually?</h3>
              <p className="text-gray-400 text-sm">
                To calculate crypto profit manually: (1) Multiply sell price by quantity to get revenue, (2) Multiply buy price by quantity to get investment, (3) Subtract investment and fees from revenue to get net profit. Formula: Net Profit = (Sell Price × Quantity) - (Buy Price × Quantity) - Fees.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What fees should I include in my calculation?</h3>
              <p className="text-gray-400 text-sm">
                Include all trading fees: exchange maker/taker fees (typically 0.1%-0.5%), withdrawal fees, network/gas fees for on-chain transactions, and any spread costs. These fees can significantly impact your actual profit, especially on smaller trades.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What is a good ROI for crypto trading?</h3>
              <p className="text-gray-400 text-sm">
                A "good" ROI varies by strategy and risk tolerance. Day traders might target 1-5% per trade, while swing traders aim for 10-30%. Long-term holders have historically seen 50-200%+ annual returns in bull markets, but crypto is highly volatile with potential for significant losses too.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">How do I calculate break-even price?</h3>
              <p className="text-gray-400 text-sm">
                Break-even price is the minimum sell price needed to recover your investment plus fees. Formula: Break-Even Price = (Total Investment + All Fees) / Quantity. Our calculator shows this automatically to help you set proper stop-loss levels.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">Does this calculator work for all cryptocurrencies?</h3>
              <p className="text-gray-400 text-sm">
                Yes! This calculator works for any cryptocurrency — Bitcoin, Ethereum, Solana, Cardano, meme coins, or any altcoin. Simply enter the buy/sell prices in USD and the quantity of coins. For specific BTC or ETH calculations, try our dedicated Bitcoin and Ethereum profit calculators.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Tips Section */}
      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2">💡 Pro Tips</h4>
        <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
          <li>Always factor in exchange fees (typically 0.1% - 0.5%) and network/gas fees</li>
          <li>Consider tax implications on crypto gains in your jurisdiction</li>
          <li>Use this calculator to plan exit strategies before entering a trade</li>
          <li>Calculate your break-even price to set proper stop-loss levels</li>
        </ul>
      </div>
    </div>
  );
};

export default CryptoProfitCalculatorTool;
